import Vue from 'vue'
import Router from 'vue-router'
import User from './User'
import * as _ from 'lodash'

import Api from '@/providers/Api'
import Breadcrumbs from '@/providers/Breadcrumbs'
import Authorization from '@/providers/Authorization'

Vue.use(Router)

/*
 * To define breadcrumbs for a route:
 * - meta: {crumbs: []} gives a default link back to the dashboard
 * - add additional crumbs as needed for deeper navigation
 * - omit if no crumbs are needed
 */

// shortcuts for commonly used breadcrumbs

const patientDetailsCrumbs = [{
    to: '/participant/details/id/:participantId',
    label: 'Manage Records: :participantName'
}]

const patientChartCrumbs = [{
    to: '/participant/chart/id/:participantId',
    label: 'At a Glance: :participantName'
}]

const router = new Router(
    {
        mode: 'history', // NB: this is important to allow either Yii or Vue to handle the same URL routes
        scrollBehavior(to, from, savedPosition) {
            return {x: 0, y: 0}
        },
        routes: [
            {
                name: 'home',
                path: '/',
                component: require('@/pages/Home').default,
                meta: {help: 'HOME'}, // this gets special treatment in HelpPane
                beforeEnter: (to, from, next) => {
                    //  check for uuid/model query parameters, if opening in iOS app
                    if (to.query) {
                        if (to.query['uuid']) {
                            localStorage.setItem('uuid', to.query['uuid'])
                        }
                        if (to.query['model']) {
                            localStorage.setItem('model', to.query['model'])
                        }
                    }
                    next()
                }
            },
            {
                path: '/terms',
                component: require('@/pages/GenericPage').default,
                props: {contentKey: 'TERMS'},
                meta: {skipCheckAuth: true}
            },
            {
                name: 'privacy',
                path: '/privacy',
                component: require('@/pages/GenericPage').default,
                props: {contentKey: 'PRIVACY'},
                meta: {skipCheckAuth: true}
            },
            {
                path: '/user/reset',
                component: require('@/pages/UserResetPassword').default,
                meta: {skipCheckAuth: true}
            },
            {
                name: 'resetConfirm', // NB: this name is used in User.checkAuth() to whitelist the route
                path: '/user/:email/reset/confirm/:confirm',
                component: require('@/pages/UserResetPassword').default,
                meta: {skipCheckAuth: true}
            },
            {
                path: '/support',
                component: require('@/pages/ContactSupport').default,
                meta: {skipCheckAuth: true}
            },
            {
                path: '/userlog/browse',
                component: require('@/pages/LogBrowse').default,
                meta: {
                    crumbs: [],
                    security: (auth) => auth.roleIs('ADMIN')
                }
            },
            // participant list for super admins
            {
                path: '/participant/admin',
                component: require('@/pages/ParticipantAdmin').default,
                meta: {
                    crumbs: [],
                    security: (auth) => auth.roleIs('ADMIN'),
                    help: 'MANAGE_PARTICIPANTS'
                }
            },
            // participant list for reseller admins
            {
                path: '/participant/resellerAdmin/:resellerId',
                component: require('@/pages/ParticipantAdmin').default,
                props: (route) => ({resellerId: +route.params.resellerId}),
                meta: {
                    crumbs: [],
                    security: (auth) => auth.roleIs('RESELLER_ADMIN'),
                    help: 'MANAGE_PARTICIPANTS'
                }
            },
            // participant list for company admins
            {
                path: '/participant/companyAdmin/:companyId',
                component: require('@/pages/ParticipantAdmin').default,
                props: (route) => ({companyId: +route.params.companyId}),
                meta: {
                    crumbs: [],
                    security: (auth) => auth.roleIs('COMPANY_ADMIN') || auth.roleIs('MULTI_COMPANY_ADMIN'),
                    help: 'MANAGE_PARTICIPANTS'
                }
            },
            // participant list for reseller admins viewing company
            {
                path: '/participant/resellerAdmin/:resellerId/company/:companyId',
                component: require('@/pages/ParticipantAdmin').default,
                props: (route) => ({
                    resellerId: +route.params.resellerId,
                    companyId: +route.params.companyId
                }),
                meta: {
                    crumbs: [
                        {to: '/company/resellerAdmin/:resellerId', label: 'Manage Companies'},
                        {to: '/resellerAdmin/:resellerId/company/id/:companyId', label: ':companyName'},
                    ],
                    security: (auth) => auth.roleIs('RESELLER_ADMIN'),
                    help: 'MANAGE_PARTICIPANTS'
                }
            },
            {
                path: '/participant/home',
                component: require('@/pages/HomeParticipant').default,
                meta: {
                    security: (auth) => auth.roleIs('PARTICIPANT')
                }
            },
            // patient pages
            {
                path: '/path',
                component: require('@/pages/MyPath').default,
                meta: {
                    help: 'PATH',
                    helpVimeoIds: [
                        {title: 'VIDEO_PATH', id: '562998605', orient: 'portrait'}
                    ],
                    security: (auth) => auth.roleIs('PARTICIPANT')
                }
            },
            {
                path: '/habits',
                component: require('@/pages/MyHabits').default,
                meta: {
                    crumbs: [],
                    help: 'HABITS',
                    helpVimeoIds: [
                        {title: 'VIDEO_HABITS', id: '562978951', orient: 'portrait'}
                    ],
                    security: (auth) => auth.roleIs('PARTICIPANT')
                }
            },
            {
                path: '/goals',
                component: require('@/pages/MyGoals').default,
                meta: {
                    crumbs: [],
                    help: 'GOALS',
                    helpVimeoIds: [
                        {title: 'VIDEO_GOALS', id: '565827160', orient: 'portrait'}
                    ],
                    security: (auth) => auth.roleIs('PARTICIPANT')
                }
            },
            {
                path: '/resources',
                component: require('@/pages/MyResources').default,
                meta: {
                    crumbs: [],
                    help: 'RESOURCES',
                    helpVimeoIds: [
                        {title: 'VIDEO_RESOURCES', id: '563012390', orient: 'portrait'}
                    ],
                    security: (auth) => auth.roleIs('PARTICIPANT')
                }
            },
            {
                path: '/lifestyle',
                component: require('@/pages/MyLifestyle').default,
                meta: {
                    crumbs: [],
                    help: 'LIFESTYLE',
                    helpVimeoIds: [
                        {title: 'VIDEO_LIFESTYLE', id: '562987752', orient: 'portrait'}
                    ],
                    security: (auth) => auth.roleIs('PARTICIPANT')
                }
            },
            {
                path: '/metrics',
                component: require('@/pages/MyMetrics').default,
                meta: {
                    crumbs: [],
                    help: 'METRICS',
                    helpVimeoIds: [
                        {title: 'VIDEO_METRICS', id: '562988634', orient: 'portrait'}
                    ],
                    security: (auth) => auth.roleIs('PARTICIPANT')
                }
            },
            {
                path: '/mindset',
                component: require('@/pages/MyMindset').default,
                meta: {
                    crumbs: [],
                    help: 'MINDSET',
                    helpVimeoIds: [
                        {title: 'VIDEO_MINDSET', id: '562995419', orient: 'portrait'}
                    ],
                    security: (auth) => auth.roleIs('PARTICIPANT')
                }
            },
            {
                path: '/reports',
                component: require('@/pages/MyReports').default,
                meta: {
                    crumbs: [],
                    help: 'REPORTS',
                    security: (auth) => auth.roleIs('PARTICIPANT')
                }
            },
            {
                path: '/notes',
                component: require('@/pages/MyNotes').default,
                meta: {
                    crumbs: [],
                    help: 'MY_NOTES',
                    security: (auth) => auth.roleIs('PARTICIPANT')
                }
            },
            {
                path: '/metricsGoogleFit',
                component: require('@/pages/MyMetrics').default,
                meta: {
                    crumbs: [],
                    help: 'METRICS',
                    security: (auth) => auth.roleIs('PARTICIPANT')
                }
            },
            {
                path: '/questionnaires',
                component: require('@/pages/MyQuestionnaires').default,
                meta: {
                    crumbs: [],
                    help: 'QUESTIONNAIRES',
                    helpVimeoIds: [
                        {title: 'VIDEO_QUESTIONNAIRES', id: '563005230', orient: 'portrait'}
                    ],
                    security: (auth) => auth.roleIs('PARTICIPANT')
                }
            },
            {
                path: '/appointments',
                component: require('@/pages/MyAppointments').default,
                meta: {
                    crumbs: [],
                    help: 'APPOINTMENTS',
                    security: (auth) => auth.roleIs('PARTICIPANT')
                }
            },
            // provider list for super admins
            {
                path: '/participant/providerList/id/:participantId',
                component: require('@/pages/ProviderList').default,
                props: (route) => ({participantId: +route.params.participantId}),
                meta: {
                    crumbs: [{
                        to: '/participant/admin',
                        labelKey: 'HEADING_MANAGE_PARTICIPANTS'
                    }],
                    security: (auth) => auth.roleIs('ADMIN')
                }
            },
            // provider list for reseller admins outside of company view
            {
                path: '/participant/resellerAdmin/:resellerId/providerList/id/:participantId',
                component: require('@/pages/ProviderList').default,
                props: (route) => ({
                    resellerId: +route.params.resellerId,
                    participantId: +route.params.participantId
                }),
                meta: {
                    crumbs: [{
                        to: '/participant/resellerAdmin/:resellerId',
                        labelKey: 'HEADING_MANAGE_PARTICIPANTS'
                    }],
                    security: (auth) => auth.roleIs('RESELLER_ADMIN')
                }
            },
            // provider list for reseller admins in company view
            {
                path: '/participant/resellerAdmin/:resellerId/company/:companyId/providerList/id/:participantId',
                component: require('@/pages/ProviderList').default,
                props: (route) => ({
                    resellerId: +route.params.resellerId,
                    companyId: +route.params.companyId,
                    participantId: +route.params.participantId
                }),
                meta: {
                    crumbs: [
                        { to: '/company/resellerAdmin/:resellerId', label: 'Manage Companies' },
                        { to: '/resellerAdmin/:resellerId/company/id/:companyId', label: ':companyName' },
                        { to: '/participant/resellerAdmin/:resellerId/company/:companyId', labelKey: 'HEADING_MANAGE_PARTICIPANTS' }
                    ],
                    security: (auth) => auth.roleIs('RESELLER_ADMIN')
                }
            },
            {
                path: '/provider/create',
                component: require('@/pages/ProviderCreate').default,
                meta: {
                    crumbs: [{to: '/', label: 'Our Patients', labelKey: 'HEADING_OUR_PATIENTS'}],
                    security: (auth) => auth.roleIs('PRACTITIONER')
                }
            },
            // edit profile for participants
            {
                path: '/participant/profile/id/:participantId',
                component: require('@/pages/ParticipantProfile').default,
                props: (route) => ({participantId: +route.params.participantId}),
                meta: {
                    crumbs: [],
                    security: (auth) => auth.roleIs('PARTICIPANT')
                }
            },
            // edit profile for practitioner
            {
                path: '/practitioner/profile/id/:participantId',
                component: require('@/pages/ParticipantProfile').default,
                props: (route) => ({participantId: +route.params.participantId}),
                meta: {
                    crumbs: [],
                    security: (auth) => auth.roleIs('PRACTITIONER')
                }
            },
            // update participant for super admins
            {
                path: '/participant/update/id/:participantId',
                component: require('@/pages/ParticipantEdit').default,
                props: (route) => ({participantId: +route.params.participantId}),
                meta: {
                    crumbs: [
                        {
                            to: `/participant/admin`,
                            labelKey: 'HEADING_MANAGE_PARTICIPANTS'
                        }
                    ],
                    security: (auth) => auth.roleIs('ADMIN')
                }
            },
            // update participant for reseller admins outside of company view
            {
                path: '/participant/resellerAdmin/:resellerId/update/id/:participantId',
                component: require('@/pages/ParticipantEdit').default,
                props: (route) => ({
                    participantId: +route.params.participantId,
                    resellerId: +route.params.resellerId,
                }),
                meta: {
                    crumbs: [
                        {
                            to: `/participant/resellerAdmin/:resellerId`,
                            labelKey: 'HEADING_MANAGE_PARTICIPANTS'
                        }
                    ],
                    security: (auth) => auth.roleIs('ADMIN') || auth.roleIs('RESELLER_ADMIN')
                }
            },
            // update participant for reseller admins in company view
            {
                path: '/participant/resellerAdmin/:resellerId/company/:companyId/update/id/:participantId',
                component: require('@/pages/ParticipantEdit').default,
                props: (route) => ({
                    participantId: +route.params.participantId,
                    resellerId: +route.params.resellerId,
                    companyId: +route.params.companyId,
                }),
                meta: {
                    crumbs: [
                        {to: '/company/resellerAdmin/:resellerId', label: 'Manage Companies'},
                        {to: '/resellerAdmin/:resellerId/company/id/:companyId', label: ':companyName'},
                        {to: '/participant/resellerAdmin/:resellerId/company/:companyId', label: 'Manage Users'}

                    ],
                    security: (auth) => auth.roleIs('RESELLER_ADMIN')
                }
            },
            {
                path: '/patient/update/id/:participantId',
                component: require('@/pages/ParticipantEdit').default,
                props: (route) => ({participantId: +route.params.participantId}),
                meta: {
                    crumbs: patientDetailsCrumbs,
                    security: (auth) => auth.roleIs('PRACTITIONER') &&
                        auth.canManagePatients(),
                }
            },
            {
                path: '/participant/create',
                component: require('@/pages/ParticipantCreate').default,
                meta: {
                    crumbs: [],
                    security: (auth) => auth.roleIs('ADMIN')
                }
            },
            {
                path: '/patient/create',
                component: require('@/pages/ParticipantCreate').default,
                meta: {
                    crumbs: [],
                    security: (auth) => auth.roleIs('PRACTITIONER') && auth.canManagePatients()
                }
            },
            {
                path: '/participant/merge',
                component: require('@/pages/ParticipantMerge').default,
                meta: {
                    crumbs: [],
                    security: (auth) => auth.roleIs('ADMIN'),
                    help: 'MERGE_PARTICIPANTS'
                }
            },
            {
                path: '/participant/import',
                component: require('@/pages/ParticipantImport').default,
                meta: {
                    crumbs: [],
                    security: (auth) => auth.roleIs('ADMIN'),
                }
            },
            {
                path: '/participant/importForPractice/practiceId/:practiceId',
                component: require('@/pages/ParticipantImport').default,
                meta: {
                    crumbs: [],
                    security: (auth) => auth.isPractitionerFromSamePractice(auth.to.params.practiceId) &&
                        auth.canManagePatients(),
                }
            },
            {
                path: '/biometrics/practiceId/:practiceId',
                component: require('@/pages/BiometricsAdmin').default,
                props: (route) => ({
                    practiceId: +route.params.practiceId,
                }),
                meta: {
                    crumbs: [],
                    help: 'PATIENT_BIOMETRICS',
                    helpVimeoIds: [
                        {title: 'VIDEO_PATIENT_BIOMETRICS', id: '562933450', orient: 'landscape'}
                    ],
                    security: (auth) => auth.isPractitionerFromSamePractice(auth.to.params.practiceId) &&
                        auth.canManagePatients(),
                }
            },
            {
                path: '/biometrics/create/practiceId/:practiceId',
                component: require('@/pages/BiometricsCreate').default,
                props: (route) => ({
                    practiceId: +route.params.practiceId,
                }),
                meta: {
                    crumbs: [
                        {
                            to: '/biometrics/practiceId/:practiceId',
                            label: 'Manage Biometrics for :practiceName'
                        },
                    ],
                    security: (auth) => auth.isPractitionerFromSamePractice(auth.to.params.practiceId) &&
                        auth.canManagePatients(),
                }
            },
            {
                path: '/biometrics/update/practiceId/:practiceId/id/:biometricsId',
                component: require('@/pages/BiometricsEdit').default,
                props: (route) => ({
                    practiceId: +route.params.practiceId,
                    biometricsId: +route.params.biometricsId
                }),
                meta: {
                    crumbs: [
                        {
                            to: '/biometrics/practiceId/:practiceId',
                            label: 'Manage Biometrics for :practiceName'
                        },
                    ],
                    security: (auth) => auth.isPractitionerFromSamePractice(auth.to.params.practiceId) &&
                        auth.canManagePatients(),
                }
            },
            {
                path: '/biometrics/importForPractice/practiceId/:practiceId',
                component: require('@/pages/BiometricsImport').default,
                meta: {
                    crumbs: [
                        {
                            to: '/biometrics/practiceId/:practiceId',
                            label: 'Manage Biometrics for :practiceName'
                        },
                    ],
                    security: (auth) => auth.isPractitionerFromSamePractice(auth.to.params.practiceId) &&
                        auth.canManagePatients(),
                }
            },
            {
                path: '/participant/details/id/:participantId',
                component: require('@/pages/PatientDetails').default,
                props: (route) => ({participantId: +route.params.participantId}),
                meta: {
                    crumbs: [],
                    help: 'PATIENT_DETAILS',
                    security: (auth) => auth.isProviderForParticipant(auth.to.params.participantId) &&
                        auth.canManagePatients(),
                }
            },
            {
                path: '/participant/chart/id/:participantId',
                component: require('@/pages/PatientChart').default,
                props: (route) => ({participantId: +route.params.participantId}),
                meta: {
                    crumbs: [],
                    help: 'PATIENT_AT_A_GLANCE',
                    helpVimeoIds: [
                        {title: 'VIDEO_PATIENT_AT_A_GLANCE', id: '561530187', orient: 'landscape'}
                    ],
                    security: (auth) => auth.isProviderForParticipant(auth.to.params.participantId)
                }
            },
            {
                path: '/participant/createNote/participantId/:participantId/type/:noteType',
                component: require('@/pages/SurveyNoteEdit').default,
                props: (route) => ({
                    participantId: +route.params.participantId,
                    noteType: route.params.noteType
                }),
                meta: {
                    crumbs: patientChartCrumbs,
                    security: (auth) => auth.isProviderForParticipant(auth.to.params.participantId) &&
                        auth.canCreateDraftSoapNote(auth.to.params.noteType),
                }
            },
            {
                path: '/participant/editNote/participantId/:participantId/id/:surveyNoteId',
                component: require('@/pages/SurveyNoteEdit').default,
                props: (route) => ({
                    participantId: +route.params.participantId,
                    surveyNoteId: +route.params.surveyNoteId,
                }),
                meta: {
                    help: 'NOTE_EDIT',
                    crumbs: patientChartCrumbs,
                    security: (auth) => auth.isProviderForParticipant(auth.to.params.participantId) &&
                        auth.canCreateDraftSoapNote(auth.to.params.noteType),
                }
            },
            // Duplicates of survey note routes for coming via RPM page
            {
                path: '/rpm/:practiceId/createNote/participantId/:participantId/type/:noteType',
                component: require('@/pages/SurveyNoteEdit').default,
                props: (route) => ({
                    participantId: +route.params.participantId,
                    noteType: route.params.noteType,
                    practiceId: route.params.practiceId,
                }),
                meta: {
                    crumbs: [
                        {to: '/rpm/:practiceId', labelKey: 'HEADING_MANAGE_PATIENT_RPM'}
                    ],
                    security: (auth) => auth.isProviderForParticipant(auth.to.params.participantId) &&
                        auth.canCreateDraftSoapNote(auth.to.params.noteType),
                }
            },
            {
                path: '/rpm/:practiceId/editNote/participantId/:participantId/id/:surveyNoteId',
                component: require('@/pages/SurveyNoteEdit').default,
                props: (route) => ({
                    participantId: +route.params.participantId,
                    surveyNoteId: +route.params.surveyNoteId,
                    practiceId: route.params.practiceId,
                }),
                meta: {
                    help: 'NOTE_EDIT',
                    crumbs: [
                        {to: '/rpm/:practiceId', labelKey: 'HEADING_MANAGE_PATIENT_RPM'}
                    ],
                    security: (auth) => auth.isProviderForParticipant(auth.to.params.participantId) &&
                        auth.canCreateDraftSoapNote(auth.to.params.noteType),
                }
            },
            // survey fill for patient
            {
                path: '/survey/fill/id/:surveyId/participantId/:participantId',
                component: require('@/pages/SurveyFill').default,
                props: (route) => ({
                    surveyId: +route.params.surveyId
                }),
                meta: {
                    crumbs: [
                        {to: '/questionnaires', label: 'My Questionnaires'},
                    ],
                    security: (auth) => auth.roleIs('PARTICIPANT')
                }
            },
            // survey create for doc
            {
                path: '/survey/doctorCreate/questionnaireCode/:questionnaireCode/participantId/:participantId',
                component: require('@/pages/SurveyCreate').default,
                props: (route) => ({
                    questionnaireCode: route.params.questionnaireCode,
                    participantId: +route.params.participantId,
                    forPractitioner: true
                }),
                meta: {
                    crumbs: patientDetailsCrumbs,
                    security: (auth) => auth.isProviderForParticipant(auth.to.params.participantId) && auth.canManagePatients()
                }
            },
            // survey fill for doc
            {
                path: '/survey/doctorFill/id/:surveyId/participantId/:participantId',
                component: require('@/pages/SurveyFill').default,
                props: (route) => ({
                    participantId: route.params.participantId,
                    surveyId: +route.params.surveyId
                }),
                meta: {
                    crumbs: patientDetailsCrumbs,
                    security: (auth) => auth.isProviderForParticipant(auth.to.params.participantId) && auth.canManagePatients()
                }
            },
            {
                path: '/survey/show/participantId/:participantId/id/:surveyId',
                component: require('@/pages/SurveyShow').default,
                props: (route) => ({surveyId: +route.params.surveyId}),
                meta: {
                    crumbs: patientDetailsCrumbs,
                    security: (auth) => auth.isMaskingAdmin()
                }
            },
            {
                path: '/survey/responses/participantId/:participantId/id/:surveyId',
                component: require('@/pages/SurveyResponses').default,
                props: (route) => ({surveyId: +route.params.surveyId}),
                meta: {
                    crumbs: patientDetailsCrumbs,
                    security: (auth) => auth.isMaskingAdmin() ||
                        (auth.isProviderForParticipant(auth.to.params.participantId) && auth.canManagePatients())
                }
            },

            // practitioner management for admin
            {
                path: '/practitioner/admin/companyId/:companyId/practiceId/:practiceId',
                component: require('@/pages/PractitionerAdmin').default,
                props: (route) => ({practiceId: +route.params.practiceId}),
                meta: {
                    crumbs: [
                        {to: '/company/admin', label: 'Manage Companies'},
                        {to: '/company/id/:companyId', label: ':companyName'},
                        {to: '/practice/admin/companyId/:companyId', label: 'Manage Practices'}
                    ],
                    security: (auth) => auth.roleIs('ADMIN'),
                    help: 'MANAGE_PRACTICES'
                }
            },
            {
                path: '/practitioner/update/companyId/:companyId/practiceId/:practiceId/id/:practitionerId',
                component: require('@/pages/PractitionerEdit').default,
                props: (route) => ({
                    companyId: +route.params.companyId,
                    practiceId: +route.params.practiceId,
                    practitionerId: +route.params.practitionerId
                }),
                meta: {
                    crumbs: [
                        {to: '/company/admin', label: 'Manage Companies'},
                        {to: '/company/id/:companyId', label: ':companyName'},
                        {to: '/practice/admin/companyId/:companyId', label: 'Manage Practices'},
                        {
                            to: '/practitioner/admin/companyId/:companyId/practiceId/:practiceId',
                            label: ':practiceName'
                        },
                    ],
                    security: (auth) => auth.roleIs('ADMIN')
                }
            },
            {
                path: '/practitioner/create/companyId/:companyId/practiceId/:practiceId',
                component: require('@/pages/PractitionerCreate').default,
                props: (route) => ({
                    companyId: +route.params.companyId,
                    practiceId: +route.params.practiceId
                }),
                meta: {
                    crumbs: [
                        {to: '/company/admin', label: 'Manage Companies'},
                        {to: '/company/id/:companyId', label: ':companyName'},
                        {to: '/practice/admin/companyId/:companyId', label: 'Manage Practices'},
                        {
                            to: '/practitioner/admin/companyId/:companyId/practiceId/:practiceId',
                            label: ':practiceName'
                        },
                    ],
                    security: (auth) => auth.roleIs('ADMIN')
                }
            },

            // practitioner management for practice manager/practitioners
            {
                path: '/practice/practitionerAdmin',
                component: require('@/pages/PractitionerAdmin').default,
                meta: {
                    crumbs: [],
                    security: (auth) => auth.canManagePracticeStaff()
                }
            },
            {
                path: '/practice/practitionerUpdate/id/:practitionerId',
                component: require('@/pages/PractitionerEdit').default,
                props: (route) => ({
                    practitionerId: +route.params.practitionerId
                }),
                meta: {
                    crumbs: [
                        {to: '/practice/practitionerAdmin', label: 'Manage Practitioners'},
                    ],
                    security: (auth) => auth.canManagePracticeStaff()
                }
            },
            {
                path: '/practice/practitionerCreate',
                component: require('@/pages/PractitionerCreate').default,
                meta: {
                    crumbs: [
                        {to: '/practice/practitionerAdmin', label: 'Manage Practitioners'},
                    ],
                    security: (auth) => auth.canManagePracticeStaff()
                }
            },

            {
                // for appointment alerts (link in email)
                path: '/practitioner/patients/filter/:filterValue',
                component: require('@/pages/PatientAdmin').default,
                props: (route) => ({filterValue: +route.params.filterValue}),
                meta: {
                    crumbs: [],
                }
            },
            // import practices and create a company for each
            {
                path: '/practice/import',
                component: require('@/pages/PracticeImport').default,
                meta: {
                    crumbs: [
                        {to: '/company/admin', label: 'Manage Companies'},
                    ],
                    security: (auth) => auth.roleIs('ADMIN'),
                }
            },
            {
                path: '/practice/admin/companyId/:companyId',
                component: require('@/pages/PracticeAdmin').default,
                props: (route) => ({companyId: +route.params.companyId}),
                meta: {
                    crumbs: [
                        {to: '/company/admin', label: 'Manage Companies'},
                        {to: '/company/id/:companyId', label: ':companyName'}
                    ],
                    security: (auth) => auth.roleIs('ADMIN')
                },
            },
            {
                path: '/practice/resellerAdmin/:resellerId/companyId/:companyId',
                component: require('@/pages/PracticeAdmin').default,
                props: (route) => ({
                    companyId: +route.params.companyId,
                    resellerId: +route.params.resellerId
                }),
                meta: {
                    crumbs: [
                        {to: '/company/resellerAdmin/:resellerId', label: 'Manage Companies'},
                        {to: '/resellerAdmin/:resellerId/company/id/:companyId', label: ':companyName'}
                    ],
                    security: (auth) => auth.isResellerAdminForCompany(auth.to.params.companyId)
                },
            },
            {
                path: '/practice/companyAdmin/companyId/:companyId',
                component: require('@/pages/PracticeAdmin').default,
                props: (route) => ({companyId: +route.params.companyId}),
                meta: {
                    crumbs: [],
                    security: (auth) => auth.isCompanyAdminFor(auth.to.params.companyId)
                },
            },
            {
                path: '/practice/profile/id/:practiceId',
                component: require('@/pages/PracticeProfile').default,
                props: (route) => ({practiceId: +route.params.practiceId}),
                meta: {
                    crumbs: [],
                    help: 'PRACTICE_PROFILE',
                    helpVimeoIds: [
                        {title: 'VIDEO_PRACTICE_PROFILE', id: '565761300', orient: 'landscape'}
                    ],
                    security: (auth) => auth.isPractitionerFromSamePractice(auth.to.params.practiceId) && auth.canManagePracticeStaff()
                }
            },
            // edit company for admins, company admins
            {
                path: '/practice/update/companyId/:companyId/id/:practiceId',
                component: require('@/pages/PracticeEdit').default,
                props: (route) => ({
                    companyId: +route.params.companyId,
                    practiceId: +route.params.practiceId,
                }),
                meta: {
                    crumbs: [{to: '/practice/companyAdmin/companyId/:companyId', label: 'Manage Practices'}],
                    security: (auth) => auth.roleIs('ADMIN') || auth.roleIs('COMPANY_ADMIN') || auth.roleIs('MULTI_COMPANY_ADMIN')
                }
            },
            // edit company for reseller admins
            {
                path: '/practice/update/resellerAdmin/:resellerId/companyId/:companyId/id/:practiceId',
                component: require('@/pages/PracticeEdit').default,
                props: (route) => ({
                    resellerId: +route.params.resellerId,
                    companyId: +route.params.companyId,
                    practiceId: +route.params.practiceId,
                }),
                meta: {
                    crumbs: [
                        {to: '/company/resellerAdmin/:resellerId', label: 'Manage Companies'},
                        {to: '/resellerAdmin/:resellerId/company/id/:companyId', label: ':companyName'},
                        {to: '/practice/resellerAdmin/:resellerId/companyId/:companyId', label: 'Manage Practices'}
                    ],
                    security: (auth) => auth.roleIs('RESELLER_ADMIN') || auth.isResellerAdminForCompany(auth.to.params.resellerId)
                }
            },
            // create practice for admin, company admin
            {
                path: '/practice/create/companyId/:companyId',
                component: require('@/pages/PracticeCreate').default,
                props: (route) => ({companyId: +route.params.companyId}),
                meta: {
                    crumbs: [{to: '/practice/companyAdmin/companyId/:companyId', label: 'Manage Practices'}],
                    security: (auth) => auth.roleIs('ADMIN') || auth.roleIs('COMPANY_ADMIN') || auth.roleIs('MULTI_COMPANY_ADMIN')
                }
            },
            // create practice for reseller admin
            {
                path: '/practice/create/companyId/:companyId/resellerAdmin/:resellerId',
                component: require('@/pages/PracticeCreate').default,
                props: (route) => ({
                    companyId: +route.params.companyId,
                    resellerId: +route.params.resellerId
                }),
                meta: {
                    crumbs: [
                        {to: '/company/resellerAdmin/:resellerId', label: 'Manage Companies'},
                        {to: '/resellerAdmin/:resellerId/company/id/:companyId', label: ':companyName'},
                        {to: '/practice/resellerAdmin/:resellerId/companyId/:companyId', label: 'Manage Practices'}
                    ],
                    security: (auth) => auth.roleIs('RESELLER_ADMIN')
                }
            },
            {
                path: '/practice/plans/id/:practiceId',
                component: require('@/pages/PracticePlans').default,
                props: (route) => ({practiceId: +route.params.practiceId}),
                meta: {
                    crumbs: [],
                    help: 'PRACTICE_PLANS',
                    security: (auth) => auth.isPractitionerFromSamePractice(auth.to.params.practiceId) &&
                        auth.canManagePracticeResources()
                }
            },
            {
                path: '/practice/superbillCodes/id/:practiceId',
                component: require('@/pages/SuperbillCodeAdmin').default,
                props: (route) => ({practiceId: +route.params.practiceId}),
                meta: {
                    crumbs: [],
                    help: 'SUPERBILL_CODES',
                    security: (auth) => auth.isPractitionerFromSamePractice(auth.to.params.practiceId) &&
                        auth.canManagePracticeResources()
                }
            },
            {
                path: '/practice/messages/id/:practiceId',
                component: require('@/pages/MessageAdmin').default,
                props: (route) => ({practiceId: +route.params.practiceId}),
                meta: {
                    crumbs: [],
                    help: 'MESSAGES',
                    helpVimeoIds: [
                        {title: 'VIDEO_PRACTICE_DOCUMENTS', id: '565271210', orient: 'landscape'}
                    ],
                    security: (auth) => auth.isPractitionerFromSamePractice(auth.to.params.practiceId) &&
                        auth.canManagePatients()
                }
            },
            {
                path: '/message/update/practiceId/:practiceId/id/:messageId',
                component: require('@/pages/MessageEdit').default,
                props: (route) => ({
                    practiceId: +route.params.practiceId,
                    messageId: +route.params.messageId
                }),
                meta: {
                    crumbs: [
                        {
                            to: '/practice/messages/id/:practiceId',
                            label: 'Manage Messages for :practiceName'
                        },
                    ],
                    security: (auth) => auth.isPractitionerFromSamePractice(auth.to.params.practiceId) &&
                        auth.canManagePatients()
                }
            },
            {
                path: '/message/create/practiceId/:practiceId',
                component: require('@/pages/MessageCreate').default,
                props: (route) => ({
                    practiceId: +route.params.practiceId,
                }),
                meta: {
                    crumbs: [
                        {
                            to: '/practice/messages/id/:practiceId',
                            label: 'Manage Messages for :practiceName'
                        },
                    ],
                    security: (auth) => auth.isPractitionerFromSamePractice(auth.to.params.practiceId) &&
                        auth.canManagePatients()
                }
            },
            {
                path: '/practice/documents/id/:practiceId',
                component: require('@/pages/PracticeDocuments').default,
                props: (route) => ({practiceId: +route.params.practiceId}),
                meta: {
                    crumbs: [],
                    help: 'PRACTICE_DOCUMENTS',
                    helpVimeoIds: [
                        {title: 'VIDEO_PRACTICE_DOCUMENTS', id: '565271210', orient: 'landscape'}

                    ],
                    security: (auth) => auth.isPractitionerFromSamePractice(auth.to.params.practiceId) &&
                        auth.canManagePatients()
                }
            },
            {
                path: '/admin/admin',
                component: require('@/pages/AdminAdmin').default,
                meta: {
                    crumbs: [],
                    security: (auth) => auth.roleIs('ADMIN')
                }
            },
            {
                path: '/admin/options',
                component: require('@/pages/AdminOptions').default,
                meta: {
                    crumbs: [],
                    security: (auth) => auth.roleIs('ADMIN')
                }
            },
            {
                path: '/resellerAdmin/admin',
                component: require('@/pages/AdminResellerAdmin').default,
                meta: {
                    crumbs: [],
                    security: (auth) => auth.roleIs('ADMIN')
                }
            },
            {
                path: '/companyAdmin/admin',
                component: require('@/pages/AdminCompanyAdmin').default,
                meta: {
                    crumbs: [],
                    security: (auth) => auth.roleIs('ADMIN')
                }
            },
            {
                path: '/companyAdmin/resellerAdmin/:resellerId',
                component: require('@/pages/AdminCompanyAdmin').default,
                props: (route) => ({resellerId: +route.params.resellerId}),
                meta: {
                    crumbs: [],
                    security: (auth) => auth.roleIs('RESELLER_ADMIN') && auth.isResellerAdminFor(auth.to.params.resellerId)
                }
            },
            {
                path: '/company/admin',
                component: require('@/pages/CompanyAdmin').default,
                meta: {
                    crumbs: [],
                    security: (auth) => auth.roleIs('ADMIN'),
                    help: 'MANAGE_COMPANIES'
                }
            },
            {
                path: '/company/resellerAdmin/:resellerId',
                component: require('@/pages/CompanyAdmin').default,
                props: (route) => ({resellerId: +route.params.resellerId}),
                meta: {
                    crumbs: [],
                    security: (auth) => auth.roleIs('RESELLER_ADMIN') && auth.isResellerAdminFor(auth.to.params.resellerId),
                    help: 'MANAGE_COMPANIES'
                }
            },
            {
                path: '/company/id/:companyId',
                component: require('@/pages/CompanyView').default,
                props: (route) => ({companyId: +route.params.companyId}),
                meta: {
                    crumbs: [
                        {
                            to: `/company/admin`,
                            label: 'Manage Companies'
                        }
                    ],
                    security: (auth) => auth.roleIs('ADMIN') || auth.isCompanyAdminFor(auth.to.params.companyId) || auth.isResellerAdminForCompany(auth.to.params.companyId)
                }
            },
            // company view for reseller admins
            {
                path: '/resellerAdmin/:resellerId/company/id/:companyId',
                component: require('@/pages/CompanyView').default,
                props: (route) => ({
                    companyId: +route.params.companyId,
                    resellerId: +route.params.resellerId
                }),
                meta: {
                    crumbs: [
                        {
                            to: `/company/resellerAdmin/:resellerId`,
                            label: 'Manage Companies'
                        }
                    ],
                    security: (auth) => auth.isResellerAdminForCompany(auth.to.params.companyId)
                }
            },
            {
                path: '/company/update/id/:companyId',
                component: require('@/pages/CompanyEdit').default,
                props: (route) => ({companyId: +route.params.companyId}),
                meta: {
                    crumbs: [
                        {to: `/company/admin`, label: 'Manage Companies'},
                        {to: '/company/id/:companyId', label: ':companyName'}
                    ],
                    security: (auth) => auth.roleIs('ADMIN') || auth.isResellerAdminForCompany(auth.to.params.companyId)
                }
            },
            // company edit for reseller admins
            {
                path: '/resellerAdmin/:resellerId/company/update/id/:companyId',
                component: require('@/pages/CompanyEdit').default,
                props: (route) => ({
                    companyId: +route.params.companyId,
                    resellerId: +route.params.resellerId
                }),
                meta: {
                    crumbs: [
                        {to: `/company/resellerAdmin/:resellerId`, label: 'Manage Companies'},
                        {to: '/resellerAdmin/:resellerId/company/id/:companyId', label: ':companyName'}
                    ],
                    security: (auth) => auth.roleIs('ADMIN') || auth.isResellerAdminForCompany(auth.to.params.companyId)
                }
            },
            {
                path: '/companyAdmin/update/id/:companyId',
                component: require('@/pages/CompanyEdit').default,
                props: (route) => ({companyId: +route.params.companyId}),
                meta: {
                    crumbs: [
                    ],
                    security: (auth) => auth.isCompanyAdminFor(auth.to.params.companyId)
                }
            },
            {
                path: '/company/emr/id/:companyId',
                component: require('@/pages/CompanyEmr').default,
                props: (route) => ({companyId: +route.params.companyId}),
                meta: {
                    crumbs: [
                        {to: `/company/admin`, label: 'Manage Companies'},
                        {to: '/company/id/:companyId', label: ':companyName'}
                    ],
                    security: (auth) => auth.roleIs('ADMIN')
                }
            },
            {
                path: '/company/create',
                component: require('@/pages/CompanyCreate').default,
                meta: {
                    crumbs: [
                        {to: `/company/admin`, label: 'Manage Companies'},
                    ],
                    security: (auth) => auth.roleIs('ADMIN')
                }
            },
            {
                path: '/company/create/reseller/:resellerId',
                component: require('@/pages/CompanyCreate').default,
                props: (route) => ({resellerId: +route.params.resellerId}),
                meta: {
                    crumbs: [
                        {to: `/company/resellerAdmin/:resellerId`, label: 'Manage Companies'},
                    ],
                    security: (auth) => auth.roleIs('RESELLER_ADMIN') && auth.isResellerAdminFor(auth.to.params.resellerId)
                }
            },
            // company plans for super admin
            {
                path: '/company/plans/id/:companyId',
                component: require('@/pages/PlanAdmin').default,
                props: (route) => ({companyId: +route.params.companyId}),
                meta: {
                    crumbs: [
                        {to: '/company/admin', label: 'Manage Companies'},
                        {to: '/company/id/:companyId', label: ':companyName'}
                    ],
                    security: (auth) => auth.roleIs('ADMIN')
                }
            },
            // company plans for reseller admin
            {
                path: '/resellerAdmin/:resellerId/company/plans/id/:companyId',
                component: require('@/pages/PlanAdmin').default,
                props: (route) => ({
                    resellerId: +route.params.resellerId,
                    companyId: +route.params.companyId
                }),
                meta: {
                    crumbs: [
                        {to: '/company/resellerAdmin/:resellerId', label: 'Manage Companies'},
                        {to: '/resellerAdmin/:resellerId/company/id/:companyId', label: ':companyName'}
                    ],
                    security: (auth) => auth.isResellerAdminForCompany(auth.to.params.companyId)
                }
            },
            // company plans for company admin
            {
                path: '/companyAdmin/plans/id/:companyId',
                component: require('@/pages/PlanAdmin').default,
                props: (route) => ({companyId: +route.params.companyId}),
                meta: {
                    crumbs: [],
                    security: (auth) => auth.isCompanyAdminFor(auth.to.params.companyId)
                }
            },
            {
                path: '/companyPractitioner/plans/id/:companyId',
                component: require('@/pages/PlanAdmin').default,
                props: (route) => ({companyId: +route.params.companyId}),
                meta: {
                    crumbs: [],
                    security: (auth) => auth.canManageCompanyPlans()
                }
            },
            {
                path: '/company/planResources/companyId/:companyId/planId/:planId',
                component: require('@/pages/PlanResources').default,
                props: (route) => ({planId: +route.params.planId}),
                meta: {
                    crumbs: [
                        {to: '/company/admin', label: 'Manage Companies'},
                        {to: '/company/id/:companyId', label: ':companyName'},
                        {to: '/company/plans/id/:companyId', label: 'Manage Plans for :companyName'}
                    ],
                    security: (auth) => auth.roleIs('ADMIN'),
                    help: 'PLAN_RESOURCES'
                }
            },
            {
                path: '/companyAdmin/planResources/companyId/:companyId/planId/:planId',
                component: require('@/pages/PlanResources').default,
                props: (route) => ({planId: +route.params.planId}),
                meta: {
                    crumbs: [
                        {to: '/companyAdmin/plans/id/:companyId', label: 'Manage Plans for :companyName'}
                    ],
                    security: (auth) => auth.isCompanyAdminFor(auth.to.params.companyId),
                    help: 'PLAN_RESOURCES'
                }
            },
            {
                path: '/companyPractitioner/planResources/companyId/:companyId/planId/:planId',
                component: require('@/pages/PlanResources').default,
                props: (route) => ({planId: +route.params.planId}),
                meta: {
                    crumbs: [
                        {to: '/companyPractitioner/plans/id/:companyId', label: 'Manage Plans for :companyName'}
                    ],
                    security: (auth) => auth.canManageCompanyPlans(),
                    help: 'PLAN_RESOURCES'
                }
            },
            // {
            //     path: '/plan/usageReport/id/:planId',
            //     component: require('@/pages/PlanUsageReport').default,
            //     meta: {
            //         crumbs: [],
            //         security: (auth) => auth.roleIs('ADMIN')
            //     }
            // },
            {
                path: '/company/aggregateReport/id/:companyId',
                component: require('@/pages/AggregateReport').default,
                props: (route) => ({companyId: +route.params.companyId}),
                meta: {
                    crumbs: [
                        {to: '/company/admin', label: 'Manage Companies'},
                    ],
                    security: (auth) => auth.roleIs('ADMIN')
                }
            },
            {
                path: '/plan/aggregateReport/companyId/:companyId/id/:planId',
                component: require('@/pages/AggregateReport').default,
                props: (route) => ({planId: +route.params.planId}),
                meta: {
                    crumbs: [
                        {to: '/company/admin', label: 'Manage Companies'},
                        {to: '/company/plans/id/:companyId', label: 'Manage Plans for :companyName'}
                    ],
                    security: (auth) => auth.roleIs('ADMIN')
                }
            },
            {
                path: '/practice/aggregateReport/id/:practiceId',
                component: require('@/pages/AggregateReport').default,
                props: (route) => ({practiceId: +route.params.practiceId}),
                meta: {
                    crumbs: [
                        {to: '/practice/admin', label: 'Manage Practices'},
                    ],
                    security: (auth) => auth.roleIs('ADMIN')
                }
            },
            {
                path: '/practitioner/aggregateReport/id/:practitionerId',
                component: require('@/pages/AggregateReport').default,
                props: (route) => ({practitionerId: +route.params.practitionerId}),
                meta: {
                    crumbs: [],
                    security: (auth) => auth.roleIs('ADMIN')
                }
            },
            {
                path: '/rpm/:practiceId',
                component: require('@/pages/MetricsAdmin').default,
                props: (route) => ({
                    practiceId: +route.params.practiceId,
                }),
                meta: {
                    crumbs: [],
                    security: (auth) => auth.isPractitionerFromSamePractice(auth.to.params.practiceId) &&
                        auth.canManagePatients()
                }
            },
            {
                path: '/practice/appointments/id/:practitionerId',
                component: require('@/pages/PractitionerAppointments').default,
                props: (route) => ({
                    practiceId: +route.params.practiceId,
                }),
                meta: {
                    crumbs:[],
                    help: 'PRACTITIONER_APPOINTMENTS'
                }
            },
            {
                path: '/video-call',
                component: require('@/pages/VideoCall').default,
                meta: {
                    crumbs:[],
                    security: (auth) => auth.canManagePatients()
                },
            },
            {
                path: '/video-call/:roomId',
                component: require('@/pages/VideoCall').default,
                props: (route) => ({
                    roomId: route.params.roomId,
                }),
                meta: {
                    crumbs:[],
                    security: (auth) => auth.canManagePatients() || auth.roleIs('PARTICIPANT')
                }
            },
            {
                path: '/practitioner/:practiceId/clientReports',
                component: require('@/pages/ClientReports').default,
                props: (route) => ({
                    practiceId: route.params.practiceId,
                }),
                meta: {
                    crumbs:[]
                }
            },
            {
                path: '/clientReports',
                component: require('@/pages/ClientReports').default,
                meta: {
                    crumbs:[],
                    security: (auth) => auth.roleIs('ADMIN')
                }
            },
            {
                path: '/practitioner/:practiceId/clientReports',
                component: require('@/pages/ClientReports').default,
                props: (route) => ({
                    practiceId: route.params.practiceId,
                }),
                meta: {
                    crumbs:[]
                }
            },
            {
                path: '/clientReports',
                component: require('@/pages/ClientReports').default,
                meta: {
                    crumbs:[],
                    security: (auth) => auth.roleIs('ADMIN')
                }
            },

            {
                path: '/conifer/:participantId?/:surveyId?/:returnUrl?/:error?/:caseIndex?',
                component: require('@/pages/Conifer').default,
                props: (route) => ({
                    participantId: +route.params.participantId,
                    surveyId: +route.params.surveyId,
                    returnUrl: route.params.returnUrl,
                    error: route.params.error,
                    caseIndex: +route.params.caseIndex,
                }),
                meta: {
                    skipCheckAuth: true
                }
            },
            {
                path: '/coniferTest/:scores?',
                component: require('@/pages/ConiferTest').default,
                props: (route) => ({
                    scores: route.params.scores
                }),
                meta: {
                    skipCheckAuth: true
                }
            },

            // default: page not found
            {
                path: '*',
                component: require('@/pages/NotFound').default,
            },
        ],
    }
)

// navigation guard - check authorization and render breadcrumbs
router.beforeEach((to, from, next) => {
    User.loadUserState() // TEMP? prevent being kicked out in development on auto refresh

    // perform substitutions in breadcrumb URLs/labels
    Breadcrumbs.renderCrumbs(to)

    // check for security rules

    let allow = true

    if (_.isFunction(to.meta.security)) {
        // set up the auth service with required params
        Authorization.to = to
        Authorization.from = from
        Authorization.next = next

        // call security function defined by route
        allow = to.meta.security(Authorization)
    }

    if (!allow) {
        Authorization.authError('Unauthorized')
    } else {
        next()
    }
})

export default router
